import { TariffSelectionStoreInitParams, TariffSelectionStoreState } from './tariff-selection-store.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, untracked } from '@angular/core';
import { ServiceStateEnum } from '@mwe/constants';
import { IProduktAuswahlDetails, IProduktSelektion, TariffOptionSap, TariffSelectionCardData } from '@mwe/models';
import { tariffSelectionStoreUtils } from './tariff-selection-store.utils';

const baseState: TariffSelectionStoreState = {
  allProducts: [],
  allPriceIndications: [],
  selectedTariffs: [],
  selectedTariffsForNewVoucher: [],
  additionalOptionGroupName: 'Zusatzoptionen',
  zipCode: '',
};

// can be added for future withStorageSync() refactoring
export const TEASER_SESSION_NAME = 'tariff-teaser';

export const TariffSelectionStore = signalStore(
  { providedIn: 'root' },
  withState<TariffSelectionStoreState>(baseState),
  withComputed(data => ({
    hasError: computed<boolean>(() => data.selectedTariffs().some(t => t.state === ServiceStateEnum.ERROR)),
    getProductSelection: computed<IProduktSelektion>(() => {
      return tariffSelectionStoreUtils.getProductSelection(data.allProducts(), data.selectedTariffs());
    }),
    getSelectedProducts: computed<IProduktAuswahlDetails[]>(() => {
      return tariffSelectionStoreUtils.getProductSelection(data.allProducts(), data.selectedTariffs(), true);
    }),
  })),
  withMethods(store => ({
    initializeStore(data: TariffSelectionStoreInitParams): void {
      patchState(store, state => {
        return {
          ...state,
          allProducts: data.allProducts,
          allPriceIndications: data.allPriceIndications,
          additionalOptionGroupName: data.additionalOptionGroupName,
          zipCode: data.zipCode,
        };
      });
    },
    updateAllPriceIndications(allPriceIndications): void {
      patchState(store, state => ({ ...state, allPriceIndications }));
    },
    initializeSelectedTariffs(selectedTariffs: TariffSelectionCardData[]): void {
      patchState(store, state => ({ ...state, selectedTariffs }));
    },
    updateAllCardLoadingStates(loadingState: ServiceStateEnum): void {
      patchState(store, state => ({
        ...state,
        selectedTariffs: state.selectedTariffs.map(t => ({ ...t, state: loadingState })),
      }));
    },
    updateCardLoadingState(tariffData: TariffSelectionCardData, state: ServiceStateEnum): void {
      const selectedTariffs = tariffSelectionStoreUtils.updateTariffs(store.selectedTariffs(), {
        ...tariffData,
        state,
      });
      patchState(store, state => ({ ...state, selectedTariffs }));
    },
    /*
    The old way:

    TariffSelectionCardStoreComponent emits newly selected options of a Tarif whenever that happens. This causes handleTariffOptionChange() of
    TariffSelectionCardLogic to be called. In this service the component VoucherFormComponent was previously persisted via (for example) TariffSelectionComponent.
    When calling handleTariffOptionChange(), the validation of the voucher as well as the voucher code itself was then fetched and retrieved in VoucherFormComponent.
    Those results were then attached to the API request (in TariffSelectionCardLogic) to fetch new prices of each changed tarif.

    The new way:

    TariffSelectionCardStoreComponent updates TariffSelectionStore whenever the tarif changes. NewVoucherFormComponent contains all the logic and correctly validates
    the code. Then, wherever NewVoucherFormComponent is contained, the mother component has to react to voucher() of NewVoucherStore and make the API call
    (for example) in TariffSelectionComponent.

    As there is no single source of truth what tariff is currently selected, these three new methods are required to simulate this.
    They combine the initial setup, tariff option change and tariff change into one signal.
    */
    setSelectedTariffsForNewVoucher(tariffsData: TariffSelectionCardData[]): void {
      // used to initially set the tariff
      patchState(store, state => ({ ...state, selectedTariffsForNewVoucher: tariffsData }));
    },
    updateSelectedTariffsForNewVoucher(tariffData: TariffSelectionCardData, newSelectedOption: TariffOptionSap): void {
      // used to set changed tariff options
      const tariffDataCopy = {
        ...tariffData,
        userSelection: [...tariffData.userSelection.filter(u => u.art !== newSelectedOption.art), newSelectedOption],
      };
      const selectedTariffsForNewVoucher = tariffSelectionStoreUtils.updateTariffs(store.selectedTariffs(), tariffDataCopy);
      const currentSelectedTariffsForNewVoucher = untracked(() => store.selectedTariffsForNewVoucher());
      const equal = selectedTariffsForNewVoucher.every(a =>
        currentSelectedTariffsForNewVoucher.some(b => tariffSelectionStoreUtils.isEqualTariff(a, b)),
      );

      // this store emits a lot of unnecessary things - this filters those updates out
      if (equal) return;

      patchState(store, state => ({ ...state, selectedTariffsForNewVoucher }));
    },
    updateSelectedTariffsForNewVoucherCategory(tariffData: TariffSelectionCardData): void {
      // used to set changed tariffs
      const selectedTariffsForNewVoucher = untracked(() => store.selectedTariffsForNewVoucher());
      const tobeUpdatedTariff = selectedTariffsForNewVoucher.find(tariff =>
        tariffSelectionStoreUtils.isEqualTariffCategory(tariff, tariffData),
      );

      if (tariffSelectionStoreUtils.isEqualTariff(tariffData, tobeUpdatedTariff)) return;

      const updatedTariffs = selectedTariffsForNewVoucher.map(tariff =>
        tariffSelectionStoreUtils.isEqualTariffCategory(tariff, tariffData) ? tariffData : tariff,
      );

      patchState(store, state => ({ ...state, selectedTariffsForNewVoucher: updatedTariffs }));
    },
    updateCardData(tariffData: TariffSelectionCardData): void {
      const selectedTariffs = tariffSelectionStoreUtils.updateTariffs(store.selectedTariffs(), tariffData);
      patchState(store, state => ({ ...state, selectedTariffs }));
    },
    // just for status-page
    updateAdditionalOptionGroupName(additionalOptionGroupName: string): void {
      patchState(store, state => ({ ...state, additionalOptionGroupName }));
    },
    reset(): void {
      patchState(store, _ => baseState);
    },
  })),
);
